import React from 'react';
import { Box, Text, Flex, Badge, Link, HStack } from '@chakra-ui/react';

// ── TAG BADGE ──────────────────────────────────────────────
export function TagBadge({ tag }) {
  const variants = {
    Review: { bg: 'rgba(0,195,255,0.15)', color: '#00c3ff' },
    Tips: { bg: 'rgba(0,119,255,0.12)', color: '#0077ff' },
    Deals: { bg: 'rgba(255,107,71,0.15)', color: '#ff6b47' },
    Guide: { bg: 'rgba(160,100,255,0.15)', color: '#c084fc' },
    default: { bg: 'rgba(255,255,255,0.08)', color: '#6b7fa0' },
  };
  const style = variants[tag] || variants.default;
  return (
    <Box
      as="span"
      display="inline-block"
      px={2.5}
      py={0.5}
      borderRadius="full"
      fontSize="0.7rem"
      fontWeight={600}
      textTransform="uppercase"
      letterSpacing="0.08em"
      bg={style.bg}
      color={style.color}
    >
      {tag}
    </Box>
  );
}

// ── VIDEO CARD ─────────────────────────────────────────────
export function VideoCard({ video, onClick }) {
  return (
    <Box
      bg="#0a0f1a"
      border="1px solid rgba(0,195,255,0.1)"
      borderRadius="14px"
      overflow="hidden"
      transition="transform 0.3s, border-color 0.3s, box-shadow 0.3s"
      cursor="pointer"
      role="group"
      onClick={onClick}
      _hover={{ transform: 'translateY(-5px)', borderColor: 'rgba(0,195,255,0.35)', boxShadow: '0 12px 40px rgba(0,195,255,0.1)' }}
    >
      {/* Thumbnail */}
      <Box
        position="relative"
        aspectRatio="16/9"
        background={video.gradient}
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="3rem"
      >
        {video.emoji}
        {/* Badge */}
        {video.badge && (
          <Box
            position="absolute"
            top={2.5}
            right={2.5}
            bg={video.badge === 'hot' ? '#ff6b47' : '#00c3ff'}
            color={video.badge === 'hot' ? '#fff' : '#000'}
            fontSize="0.68rem"
            fontWeight={700}
            px={2}
            py={0.5}
            borderRadius="full"
            textTransform="uppercase"
          >
            {video.badge}
          </Box>
        )}
        {/* Duration */}
        <Box
          position="absolute"
          bottom={2}
          right={2}
          bg="rgba(0,0,0,0.7)"
          color="white"
          fontSize="0.7rem"
          px={1.5}
          py={0.5}
          borderRadius="4px"
        >
          {video.duration}
        </Box>
        {/* Hover overlay */}
        <Box
          position="absolute"
          inset={0}
          bg="rgba(0,0,0,0.35)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          opacity={0}
          transition="opacity 0.3s"
          _groupHover={{ opacity: 1 }}
        >
          <Flex
            w="52px"
            h="52px"
            bg="rgba(0,195,255,0.9)"
            borderRadius="50%"
            alignItems="center"
            justifyContent="center"
            fontSize="1.2rem"
            transform="scale(0.8)"
            transition="transform 0.2s"
            _groupHover={{ transform: 'scale(1)' }}
          >
            ▶
          </Flex>
        </Box>
      </Box>
      {/* Info */}
      <Box p={4.5}>
        <Text color="#00c3ff" fontSize="0.7rem" fontWeight={600} letterSpacing="0.1em" textTransform="uppercase" mb={1.5}>
          {video.category}
        </Text>
        <Text fontFamily="'Fraunces', serif" fontSize="1rem" fontWeight={700} lineHeight={1.4}>
          {video.title}
        </Text>
        <Flex align="center" gap={2.5} mt={3} fontSize="0.78rem" color="#6b7fa0">
          <Text>👁 {video.views}</Text>
          <Text>•</Text>
          <Text>⏱ {video.duration}</Text>
        </Flex>
      </Box>
    </Box>
  );
}

// ── ARTICLE CARD (large) ───────────────────────────────────
export function ArticleCardLarge({ article, onClick }) {
  return (
    <Box
      bg="#0a0f1a"
      border="1px solid rgba(0,195,255,0.1)"
      borderRadius="14px"
      overflow="hidden"
      transition="transform 0.3s, border-color 0.3s, box-shadow 0.3s"
      cursor="pointer"
      onClick={onClick}
      _hover={{ transform: 'translateY(-4px)', borderColor: 'rgba(0,195,255,0.3)', boxShadow: '0 12px 40px rgba(0,195,255,0.08)' }}
    >
      {/* Image */}
      <Box
        h="260px"
        background={article.gradient}
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="3.5rem"
        position="relative"
      >
        {article.emoji}
        <Box position="absolute" bottom={0} left={0} right={0} h="80px" background="linear-gradient(transparent, #0a0f1a)" />
      </Box>
      <Box p={5.5}>
        <TagBadge tag={article.tags?.[0] || article.category} />
        <Text fontFamily="'Fraunces', serif" fontSize="1.5rem" fontWeight={700} lineHeight={1.4} mt={2} mb={2}>
          {article.title}
        </Text>
        <Text color="#6b7fa0" fontSize="0.88rem" lineHeight={1.7}>{article.excerpt}</Text>
        <Flex align="center" justify="space-between" mt={4} fontSize="0.78rem" color="#6b7fa0">
          <Text>{article.date}</Text>
          <Text>⏱ {article.readTime} read</Text>
        </Flex>
      </Box>
    </Box>
  );
}

// ── ARTICLE CARD (mini) ────────────────────────────────────
export function ArticleCardMini({ article, onClick }) {
  return (
    <Box
      bg="#0a0f1a"
      border="1px solid rgba(0,195,255,0.1)"
      borderRadius="12px"
      overflow="hidden"
      transition="transform 0.3s, border-color 0.3s"
      cursor="pointer"
      onClick={onClick}
      _hover={{ transform: 'translateY(-3px)', borderColor: 'rgba(0,195,255,0.25)' }}
    >
      <Flex>
        <Box
          w="90px"
          flexShrink={0}
          background={article.gradient}
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontSize="2rem"
        >
          {article.emoji}
        </Box>
        <Box p={3.5}>
          <TagBadge tag={article.tags?.[0] || article.category} />
          <Text fontFamily="'Fraunces', serif" fontSize="0.9rem" fontWeight={700} lineHeight={1.4} mt={1.5}>
            {article.title}
          </Text>
          <Text color="#6b7fa0" fontSize="0.75rem" mt={1.5}>{article.date} · {article.readTime}</Text>
        </Box>
      </Flex>
    </Box>
  );
}

// ── PRODUCT CARD ───────────────────────────────────────────
export function ProductCard({ product }) {
  const badgeStyles = {
    pick: { label: '⭐ My Pick', bg: '#00c3ff', color: '#000' },
    hot: { label: '🔥 Hot Deal', bg: '#ff6b47', color: '#fff' },
    new: { label: '✨ New', bg: '#0077ff', color: '#fff' },
  };
  const badge = badgeStyles[product.badge];

  return (
    <Box
      bg="#0a0f1a"
      border="1px solid rgba(0,195,255,0.1)"
      borderRadius="16px"
      overflow="hidden"
      transition="transform 0.3s, border-color 0.3s, box-shadow 0.3s"
      _hover={{ transform: 'translateY(-5px)', borderColor: 'rgba(0,195,255,0.35)', boxShadow: '0 16px 48px rgba(0,195,255,0.1)' }}
      display="flex"
      flexDirection="column"
    >
      {/* Gradient area */}
      <Box
        h="140px"
        background={product.gradient}
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="3.5rem"
        position="relative"
      >
        {product.emoji}
        {badge && (
          <Box
            position="absolute"
            top={3}
            left={3}
            bg={badge.bg}
            color={badge.color}
            fontSize="0.68rem"
            fontWeight={700}
            px={2}
            py={0.5}
            borderRadius="full"
          >
            {badge.label}
          </Box>
        )}
      </Box>

      <Box p={5} flex={1} display="flex" flexDirection="column">
        <Text fontFamily="'Fraunces', serif" fontSize="1rem" fontWeight={700} mb={1.5}>
          {product.name}
        </Text>
        <Text color="#6b7fa0" fontSize="0.83rem" lineHeight={1.6} flex={1}>
          {product.desc}
        </Text>

        {/* Rating */}
        <Flex align="center" gap={1.5} mt={3} mb={3}>
          <Text color="#00c3ff" fontSize="0.85rem">{'★'.repeat(Math.floor(product.rating))}</Text>
          <Text color="#6b7fa0" fontSize="0.75rem">{product.rating}</Text>
        </Flex>

        {/* Price */}
        <Flex align="center" gap={3} mb={4}>
          <Text fontSize="1.25rem" fontWeight={800} color="#00c3ff">{product.price}</Text>
          {product.oldPrice && (
            <Text fontSize="0.85rem" color="#6b7fa0" textDecoration="line-through">{product.oldPrice}</Text>
          )}
        </Flex>

        <Link
          href={product.link}
          target="_blank"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="#00c3ff"
          color="#000"
          fontWeight={700}
          fontSize="0.88rem"
          py={2.5}
          borderRadius="8px"
          transition="all 0.2s"
          _hover={{ transform: 'translateY(-1px)', boxShadow: '0 8px 24px rgba(0,195,255,0.4)', textDecoration: 'none', bg: '#33d0ff' }}
        >
          View Deal →
        </Link>
      </Box>
    </Box>
  );
}

// ── SECTION HEADER ─────────────────────────────────────────
export function SectionHeader({ label, title, desc }) {
  return (
    <Box mb={2}>
      <Flex align="center" gap={2.5} mb={2.5}>
        <Box w="26px" h="2px" bgGradient="linear(to-r, #0077ff, #00c3ff)" borderRadius="2px" />
        <Text fontSize="0.72rem" fontWeight={600} letterSpacing="0.15em" textTransform="uppercase" color="#00c3ff">
          {label}
        </Text>
      </Flex>
      <Text
        as="h2"
        fontFamily="'Fraunces', serif"
        fontSize={{ base: '1.9rem', md: '2.5rem', lg: '3rem' }}
        fontWeight={900}
        letterSpacing="-0.02em"
        dangerouslySetInnerHTML={{ __html: title }}
      />
      {desc && (
        <Text color="#6b7fa0" maxW="520px" mt={2.5} fontWeight={300}>
          {desc}
        </Text>
      )}
    </Box>
  );
}
